import { Dispatch, SetStateAction } from 'react'
import { NavigateFunction } from 'react-router-dom'
import { Node } from 'reactflow'
import { VehicleService } from '../../../services/VehicleService'
import { VehicleResearchResponse, VehicleType } from '../../../types/vehicle'
import { ResearchTreeConfig } from '../researchTree/ResearchTree'

export const buildNormalNodes:
	(userId: number,
		data: VehicleResearchResponse[],
		setTriggerUpdate: Dispatch<SetStateAction<number>>,
		navigate: NavigateFunction,
		hoverCarNode?: Function,
	) => (Node<any, string | undefined>)[] | undefined
	= (userId: number,
		data: VehicleResearchResponse[],
		setTriggerUpdate: Dispatch<SetStateAction<number>>,
		navigate: NavigateFunction,
		hoverCarNode?: Function,
	) => {
		const nodes: Node<any, string | undefined>[] = []

		const map = (vehicle: VehicleResearchResponse) => {
			if (vehicle.donatePrice > 0) return undefined

			return {
				id: vehicle.id.toString(),
				data: {
					name: vehicle.name,
					brand: vehicle.brand,
					rank: vehicle.rank,
					img: vehicle.modelName,
					checked: getIsChecked(vehicle),
					button: getButton(userId, vehicle, data),
					setTriggerUpdate: setTriggerUpdate,
					onClick: getOnClickFunction(vehicle, navigate),
					experience: vehicle.totalXp,
					hoverCarNode: hoverCarNode
				},
				position: getPosition(
					vehicle.rank,
					vehicle.type,
					getSiblingIndex(vehicle, data, nodes)
				),
				type: 'car'
			}
		}

		data.forEach((vehicle: VehicleResearchResponse) => {
			const node = map(vehicle)
			if (node) nodes.push(node)
		})

		return nodes
	}

const getOnClickFunction = (vehicle: VehicleResearchResponse, navigate: NavigateFunction) => {
	return () => {
		navigate(`/modules-research?id=${vehicle.id}`)
	}
}

const getButton = (userId: number, vehicle: VehicleResearchResponse, vehicles: VehicleResearchResponse[]) => {

	let activeParent
	const isAtLeastOneParentResearchedOrBought = () => {
		return vehicle.parentIds.some((parentId: number) => {
			const parentVehicle = vehicles.find((vehicle: VehicleResearchResponse) => vehicle.id === parentId)
			if (!parentVehicle) return false
			activeParent = parentVehicle
			return parentVehicle.researched || parentVehicle.bought
		})
	}

	if (!isAtLeastOneParentResearchedOrBought()) return undefined

	if (activeParent && !vehicle.bought && !vehicle.researched) {
		return {
			type: 'RESEARCH',
			price: vehicle.xpPrice,
			onClick: async () => VehicleService.researchVehicle(vehicle.id)
		}
	}

	if (activeParent && vehicle.researched) {
		return {
			type: 'BUY',
			price: vehicle.price,
			onClick: async () => VehicleService.buyVehicle(vehicle.id)
		}
	}
}

const getIsChecked = (vehicle: VehicleResearchResponse) => {
	return vehicle.bought
}

const getSiblingIndex = (vehicle: VehicleResearchResponse, vehicles: VehicleResearchResponse[], nodes: Node<any, string | undefined>[]) => {
	if (vehicle.parentIds.length === 0) return 0

	const isAtLeastOneSimilarParent = (vehicle1: VehicleResearchResponse, vehicle2: VehicleResearchResponse) => {
		return vehicle1.parentIds.some((parentId: number) => vehicle2.parentIds.includes(parentId))
	}

	const isSameType = (vehicle1: VehicleResearchResponse, vehicle2: VehicleResearchResponse) => {
		return vehicle1.type === vehicle2.type
	}

	const isSameRank = (vehicle1: VehicleResearchResponse, vehicle2: VehicleResearchResponse) => {
		return vehicle1.rank === vehicle2.rank
	}

	const isAtLeastOneAlreadyInList = (vehicle2: VehicleResearchResponse) => {
		return nodes.some((node: Node<any, string | undefined>) => node.id === vehicle2.id.toString())
	}

	const vehiclesSortedWithSiblings = vehicles.filter((otherVehicle: VehicleResearchResponse) => {
		return isSameType(vehicle, otherVehicle)
			&& isSameRank(vehicle, otherVehicle)
			&& isAtLeastOneSimilarParent(vehicle, otherVehicle)
			&& isAtLeastOneAlreadyInList(otherVehicle)
	})

	return vehiclesSortedWithSiblings.length
}

const getPosition:
	(rank: number, type: VehicleType, siblingIndex: number) => { x: number, y: number }
	= (rank: number, type: VehicleType, siblingIndex: number) => {

		const x = ResearchTreeConfig.HORIZONTAL_SPACE_BETWEEN_VEHICLES * (rank - 1)


		const getTypeIndex = () => {
			switch (type) {
				case 'SPORT':
					return 0
				case 'PREMIUM':
					return 1
				case 'COMFORT':
					return 2
				case 'SUV':
					return 3
				case 'OFFROAD':
					return 4
			}
		}
		const verticalSpaceByType = ResearchTreeConfig.VERTICAL_SPACE_BETWEEN_TYPES *
			getTypeIndex()

		const verticalSpaceBySibling = ResearchTreeConfig.VERTICAL_SPACE_BETWEEN_SIBLINGS * siblingIndex
		const y = verticalSpaceByType + verticalSpaceBySibling

		return { x, y }
	}
